import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAlert } from '../context/AlertContext';
import useModalScrollLock from '../hooks/useModalScrollLock';
import html2pdf from 'html2pdf.js';
import { API_URL } from '../config/api';
import AgencyDropdown from './AgencyDropdown';

// Exports are requested with responseType 'blob', so a JSON error body comes back as a
// Blob and error.response.data reads as "[object Blob]" - the server's actual message
// never reaches the user. Pull it back out, and name the no-response case explicitly:
// ERR_NETWORK here means the request left and the reply never landed intact, which is a
// very different problem from the server refusing it.
const describeExportError = async (error) => {
  if (error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.error || parsed.message || text;
      } catch {
        return text.slice(0, 300);
      }
    } catch {
      return null;
    }
  }

  if (error.code === 'ERR_NETWORK') {
    return 'the download was interrupted before it finished. The server sent the file, but it did not arrive intact - this is usually security software or a proxy on the workstation blocking the download rather than a problem with the record data.';
  }

  return error.response?.data?.error || error.message || null;
};

const DetentionMonthlySummary = ({ authHeader }) => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedFacility, setSelectedFacility] = useState(''); // New facility filter
  const [availableFacilities, setAvailableFacilities] = useState([]); // List of facilities
  const [availableMonths, setAvailableMonths] = useState([]); // List of months with data
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [viewMode, setViewMode] = useState('detention'); // 'detention' or 'immigration'
  const [filterBy] = useState('report_month'); // Fixed to report_month only
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelectedMonth, setExportSelectedMonth] = useState('');

  // DIO Analysis modal states
  const [showDIOAnalysisModal, setShowDIOAnalysisModal] = useState(false);
  const [dioAnalysisSelectedMonth, setDIOAnalysisSelectedMonth] = useState('');

  // TFM Arrest Export modal states
  const [showTFMArrestModal, setShowTFMArrestModal] = useState(false);
  const [tfmArrestSelectedMonth, setTFMArrestSelectedMonth] = useState('');



  // FDLE Export Configuration States
  const [showFDLEExportModal, setShowFDLEExportModal] = useState(false);
  const [fdleExportMonth, setFDLEExportMonth] = useState('');
  const [fdleExportConfig, setFDLEExportConfig] = useState({
    agencyORI: '', // Will be auto-populated from environment
    reimbursementType: 'Per Stay', // Default value
    submitterTitle: '',
    submitterFirstName: '',
    submitterLastName: '',
    submitterIdNumber: '',
    submitterPhone: '',
    submitterEmail: ''
  });

  // FDLE Immigration Summary Configuration States
  const [showFDLEImmigrationSummaryModal, setShowFDLEImmigrationSummaryModal] = useState(false);
  const [fdleImmigrationSummaryMonth, setFDLEImmigrationSummaryMonth] = useState('');
  const [fdleImmigrationSummaryConfig, setFDLEImmigrationSummaryConfig] = useState({
    agencyORI: '', // Will be auto-populated from environment
    agencyName: '', // Will be auto-populated from environment
    hasDIOs: 'Yes',
    numberOfDIOs: '0',
    numberOfInvestigations: '',
    numberOfArrestInvestigations: '',
    numberOfCivilOnlyArrests: '',
    submitterTitle: '',
    submitterFirstName: '',
    submitterLastName: '',
    submitterIdNumber: '',
    submitterPhone: '',
    submitterEmail: ''
  });





  // FDLE Form Modal States
  const [showFDLEFormModal, setShowFDLEFormModal] = useState(false);
  const [fdleFormData, setFDLEFormData] = useState(null);





  // Auto-populate FDLE configurations with environment variables
  useEffect(() => {
    const fetchAgencyDefaults = async () => {
      try {
        await axios.get(`${API_URL}/detention/config/agency-defaults`);

        // Don't auto-populate agency info - let user fill manually
        setFDLEExportConfig(prev => ({
          ...prev,
          reimbursementType: 'Per Stay' // Ensure default value
        }));

        // Auto-populate immigration summary config - only keep statistical fields prefilled
        setFDLEImmigrationSummaryConfig(prev => ({
          ...prev,
          // Don't auto-populate agency or submitter fields - let user fill them manually
        }));

      } catch (error) {
        // Set fallback defaults without agency information
        setFDLEExportConfig(prev => ({
          ...prev,
          reimbursementType: 'Per Stay'
        }));
      }
    };

    fetchAgencyDefaults();
  }, []); // Run once on component mount

  // Ensure immigration summary fields are populated when modal opens
  useEffect(() => {
    if (showFDLEImmigrationSummaryModal) {
      const ensureFieldsPopulated = async () => {
        try {
          await axios.get(`${API_URL}/detention/config/agency-defaults`);

          // Don't auto-populate any fields - let user fill them manually
          setFDLEImmigrationSummaryConfig(prev => ({
            ...prev
            // No auto-population
          }));
        } catch (error) {
          // Silently handle error - fallback values will be used
        }
      };

      ensureFieldsPopulated();
    }
  }, [showFDLEImmigrationSummaryModal]);

  // Auto-populate current month when FDLE modals open
  useEffect(() => {
    if (showFDLEExportModal && !fdleExportMonth) {
      const getCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth()).padStart(2, '0'); // Previous month
        return `${year}-${month}`;
      };
      setFDLEExportMonth(getCurrentMonth());
    }
  }, [showFDLEExportModal, fdleExportMonth]);

  useEffect(() => {
    if (showFDLEImmigrationSummaryModal && !fdleImmigrationSummaryMonth) {
      const getCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth()).padStart(2, '0'); // Previous month
        return `${year}-${month}`;
      };
      setFDLEImmigrationSummaryMonth(getCurrentMonth());
    }
  }, [showFDLEImmigrationSummaryModal, fdleImmigrationSummaryMonth]);






  // Prevent body scrolling when any modal is open
  const anyModalOpen = showExportModal || showFDLEExportModal ||
                       showFDLEImmigrationSummaryModal || showFDLEFormModal;
  useModalScrollLock(anyModalOpen);

  const { showError, showSuccess } = useAlert();
  const printRef = useRef();
  const fetchingSummary = useRef(false); // Track if we're already fetching summary





  // Fetch DIO statistics from user management for a specific month
  const fetchDioStats = async (month = null) => {
    // setLoadingDioStats(true); // Removed - no longer needed
    try {
      const token = localStorage.getItem('idToken');
      let apiUrl = `${API_URL}/admin/users/dio-stats`;

      // Add month parameter if provided
      if (month) {
        apiUrl += `?month=${month}`;
      }

      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // DIO stats fetched successfully

        return response.data.data;
      } else {
        return null;
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showError('You do not have permission to view DIO statistics.');
      } else if (error.response?.status === 401) {
        showError('Authentication error. Please try refreshing the page.');
      } else {
        showError('Failed to load DIO statistics');
      }
      return null;
    } finally {
      // setLoadingDioStats(false); // Removed - no longer needed
    }
  };

  // Fetch immigration statistics for a specific month to auto-populate investigation counts
  const fetchImmigrationStats = async (month) => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(`${API_URL}/immigrants/monthly-stats/${month}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data) {
        return {
          totalInvestigations: response.data.totalInvestigations || 0,
          arrestInvestigations: response.data.arrestInvestigations || 0,
          civilImmigrationArrests: response.data.civilImmigrationArrests || 0
        };
      }
      return { totalInvestigations: 0, arrestInvestigations: 0, civilImmigrationArrests: 0 };
    } catch (error) {
      showError('Error fetching immigration statistics: ' + error.message);
      return { totalInvestigations: 0, arrestInvestigations: 0, civilImmigrationArrests: 0 };
    }
  };

  // Fetch available months from database
  const fetchAvailableMonths = useCallback(async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(`${API_URL}/detention/available-months`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.data) {
        // Transform the data to match the expected format
        const months = response.data.data.map(monthData => ({
          month_key: monthData.month_key,
          month_label: monthData.month_label,
          record_count: monthData.record_count || 0
        }));

        // Sort months in descending order (most recent first)
        months.sort((a, b) => b.month_key.localeCompare(a.month_key));

        setAvailableMonths(months);

        // Auto-select the most recent month if none is selected
        if (!selectedMonth && months.length > 0) {
          setSelectedMonth(months[0].month_key);
        }
      } else {
        console.warn('No available months data received');
        setAvailableMonths([]);
      }
    } catch (error) {
      console.error('Error fetching available months:', error);
      // Fallback to current month if API fails
      const currentDate = new Date();
      const currentMonth = currentDate.toISOString().slice(0, 7);
      const currentMonthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      setAvailableMonths([{
        month_key: currentMonth,
        month_label: currentMonthLabel,
        record_count: 0
      }]);

      if (!selectedMonth) {
        setSelectedMonth(currentMonth);
      }
    }
  }, [selectedMonth]);

  // Fetch available facilities for the dropdown
  const fetchAvailableFacilities = useCallback(async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(`${API_URL}/detention/facilities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setAvailableFacilities(response.data.facilities || []);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setAvailableFacilities([]);
    }
  }, []);

  // Fetch monthly summary data from the backend
  const fetchMonthlySummary = useCallback(async (month) => {
    if (fetchingSummary.current) {
      console.log('Already fetching summary, skipping...');
      return;
    }

    fetchingSummary.current = true;
    setLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      // Build API URL with facility filter if selected
      let apiUrl = `${API_URL}/detention/monthly-summary/${month}?filterBy=${filterBy}`;
      if (selectedFacility) {
        apiUrl += `&facility=${encodeURIComponent(selectedFacility)}`;
      }

      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSummaryData(response.data.data);
      } else {
        console.error('Failed to load monthly summary data');
        setSummaryData(null);
      }
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
      if (error.response?.status === 403) {
        console.error('You do not have permission to view detention summaries. This feature is only available to Detention, Admin, and Security Admin users.');
      } else if (error.response?.status === 401) {
        console.error('Authentication error. Please try refreshing the page.');
      } else {
        console.error('Failed to load monthly summary data');
      }
      setSummaryData(null);
    } finally {
      setLoading(false);
      fetchingSummary.current = false;
    }
  }, [filterBy, selectedFacility]); // Include selectedFacility in dependency array



  // Fetch available months and facilities on component mount
  useEffect(() => {
    fetchAvailableMonths();
    fetchAvailableFacilities();
  }, [fetchAvailableMonths, fetchAvailableFacilities]);

  // Fetch summary data when selected month, filter, or facility changes
  useEffect(() => {
    if (selectedMonth) {
      fetchMonthlySummary(selectedMonth);
    }
  }, [selectedMonth, filterBy, selectedFacility, fetchMonthlySummary]);









  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
















  const formatPercentage = (numerator, denominator) => {
    if (denominator === 0) return '0%';
    return `${Math.round((numerator / denominator) * 100)}%`;
  };

  const generatePDF = async () => {
    if (!summaryData) {
      showError('No summary data available to print');
      return;
    }

    setGeneratingPdf(true);

    try {
      const element = printRef.current;

      // PDF options - back to original simple version
      const options = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `detention-summary-${summaryData.reportMonth.replace(' ', '-').toLowerCase()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: false
        },
        jsPDF: {
          unit: 'in',
          format: 'letter',
          orientation: 'portrait',
          putOnlyUsedFonts: true,
          floatPrecision: 16
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Generate and download PDF
      await html2pdf().set(options).from(element).save();
      showSuccess('PDF generated successfully!');

    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };



  // Note: Available facilities are now loaded from database via fetchAvailableFacilities()

  // Export highest offense classification for individuals with ICE detainers
  const exportOffenseClassification = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export offense classification data.');
      return;
    }

    setExportLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(`${API_URL}/detention/export-offense-classification/${monthToExport}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `highest-offense-classification-${monthToExport}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(`Highest offense classification data for ${monthToExport} exported successfully!`);
    } catch (error) {
      console.error('Error exporting offense classification data:', error);
      const detail = await describeExportError(error);
      showError(`Failed to export offense classification data for ${monthToExport}${detail ? `: ${detail}` : '. Please try again.'}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Export detention data in FDLE Excel format - opens configuration modal first
  const exportFDLEDetentionData = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export FDLE detention data.');
      return;
    }

    // Get agency defaults to ensure all fields are populated with immigration submitter data
    try {
      await axios.get(`${API_URL}/detention/config/agency-defaults`);

      // Don't prefill agency info - let user fill manually
      setFDLEExportConfig({
        agencyORI: '',
        reimbursementType: 'Per Stay',
        submitterTitle: '',
        submitterFirstName: '',
        submitterLastName: '',
        submitterIdNumber: '',
        submitterPhone: '',
        submitterEmail: ''
      });
    } catch (error) {
      // Fallback configuration without agency info
      setFDLEExportConfig({
        agencyORI: '',
        reimbursementType: 'Per Stay',
        submitterTitle: '',
        submitterFirstName: '',
        submitterLastName: '',
        submitterIdNumber: '',
        submitterPhone: '',
        submitterEmail: ''
      });
    }

    // Set the month and open the FDLE configuration modal
    // Note: availableFacilities are already loaded from database via fetchAvailableFacilities()
    setFDLEExportMonth(monthToExport);
    setShowFDLEExportModal(true);
  };

  // Export immigration data in FDLE Excel format - direct export using existing officer data
  const exportFDLEImmigrationData = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export FDLE immigration data.');
      return;
    }

    setExportLoading(true);
    try {


      const token = localStorage.getItem('idToken');
      const apiUrl = `${API_URL}/immigrants/monthly-export/${monthToExport}?format=fdle`;



      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Important for ZIP files
      });

      // Handle ZIP download
      const blob = new Blob([response.data], {
        type: 'application/zip'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fdle-form-897-entries-${monthToExport}.zip`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(` FDLE immigration data for ${monthToExport} exported successfully as ZIP file (Excel + Photos)!`);
    } catch (error) {
      console.error('Error exporting FDLE immigration data:', error);
      const detail = await describeExportError(error);
      showError(`Failed to export FDLE immigration data for ${monthToExport}${detail ? `: ${detail}` : '. Please try again.'}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Export TFM Arrest data - exports all DIO records with TFM Arrest Sheet information
  const exportTFMArrestData = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export TFM Arrest data.');
      return;
    }

    setExportLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      const apiUrl = `${API_URL}/immigrants/tfm-arrest-export/${monthToExport}`;

      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Important for CSV files
      });

      // Handle CSV download
      const blob = new Blob([response.data], {
        type: 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TFM-Arrest-Export-${monthToExport}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(` TFM Arrest data for ${monthToExport} exported successfully!`);
    } catch (error) {
      console.error('Error exporting TFM Arrest data:', error);
      if (error.response && error.response.status === 404) {
        showError(`No TFM Arrest records found for ${monthToExport}. Records must have DIO = Yes AND Non-Criminal Arrest = Yes.`);
      } else {
        const detail = await describeExportError(error);
        showError(`Failed to export TFM Arrest data for ${monthToExport}${detail ? `: ${detail}` : '. Please try again.'}`);
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Export DIO analysis report - shows which contacts were DIO vs non-DIO
  const exportDIOAnalysis = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export DIO analysis.');
      return;
    }

    setExportLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(`${API_URL}/immigrants/dio-analysis/${monthToExport}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Important for CSV files
      });

      // Handle CSV download
      const blob = new Blob([response.data], {
        type: 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dio-analysis-${monthToExport}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(`DIO analysis for ${monthToExport} exported successfully! This report shows which contacts were DIO vs non-DIO investigations.`);
    } catch (error) {
      console.error('Error exporting DIO analysis:', error);
      const detail = await describeExportError(error);
      showError(`Failed to export DIO analysis for ${monthToExport}${detail ? `: ${detail}` : '. Please try again.'}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Export immigration monthly summary in FDLE Excel format - opens configuration modal first
  const exportFDLEImmigrationSummary = async (monthToExport) => {
    if (!monthToExport) {
      showError('Please select a month to export FDLE immigration summary.');
      return;
    }



    // Fetch DIO statistics and immigration statistics to auto-populate fields
    const [dioStatistics, immigrationStatistics] = await Promise.all([
      fetchDioStats(monthToExport),
      fetchImmigrationStats(monthToExport)
    ]);

    // Get agency defaults to ensure all fields are populated with immigration submitter data
    try {
      await axios.get(`${API_URL}/detention/config/agency-defaults`);

      // Reset configuration with DIO data and immigration statistics only - no prefilled agency/submitter info
      setFDLEImmigrationSummaryConfig({
        agencyORI: '',
        agencyName: '',
        hasDIOs: dioStatistics?.hasDIOs ? 'Yes' : 'No',
        numberOfDIOs: '0',
        numberOfInvestigations: immigrationStatistics?.totalInvestigations?.toString() || '0',
        numberOfArrestInvestigations: immigrationStatistics?.arrestInvestigations?.toString() || '0',
        numberOfCivilOnlyArrests: immigrationStatistics?.civilImmigrationArrests?.toString() || '0',
        submitterTitle: '',
        submitterFirstName: '',
        submitterLastName: '',
        submitterIdNumber: '',
        submitterPhone: '',
        submitterEmail: ''
      });
    } catch (error) {
      // Fallback configuration with no prefilled agency/submitter info
      setFDLEImmigrationSummaryConfig({
        agencyORI: '',
        agencyName: '',
        hasDIOs: dioStatistics?.hasDIOs ? 'Yes' : 'No',
        numberOfDIOs: '0',
        numberOfInvestigations: immigrationStatistics?.totalInvestigations?.toString() || '0',
        numberOfArrestInvestigations: immigrationStatistics?.arrestInvestigations?.toString() || '0',
        numberOfCivilOnlyArrests: immigrationStatistics?.civilImmigrationArrests?.toString() || '0',
        submitterTitle: '',
        submitterFirstName: '',
        submitterLastName: '',
        submitterIdNumber: '',
        submitterPhone: '',
        submitterEmail: ''
      });
    }



    // Set the month and open the FDLE immigration summary configuration modal
    setFDLEImmigrationSummaryMonth(monthToExport);

    // Small delay to ensure state is updated before opening modal
    setTimeout(() => {
      setShowFDLEImmigrationSummaryModal(true);
    }, 100);
  };

  // Format phone number to +1XXXXXXXXXX format (expects 10 digits)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');

    // Should be exactly 10 digits
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }

    // If not 10 digits, return empty (validation will catch this)
    return '';
  };

  // Handle phone input with formatting like immigrant form
  const handlePhoneInput = (e) => {
    const value = e.target.value;
    // Remove all non-digit characters
    let digitsOnly = value.replace(/\D/g, '');
    // Limit to 10 digits
    digitsOnly = digitsOnly.slice(0, 10);

    // Format as XXX-XXX-XXXX while typing
    let formattedValue = digitsOnly;
    if (digitsOnly.length >= 6) {
      formattedValue = digitsOnly.slice(0, 3) + '-' + digitsOnly.slice(3, 6) + '-' + digitsOnly.slice(6, 10);
    } else if (digitsOnly.length >= 3) {
      formattedValue = digitsOnly.slice(0, 3) + '-' + digitsOnly.slice(3);
    }

    setFDLEExportConfig(prev => ({
      ...prev,
      submitterPhone: formattedValue
    }));
  };

  // Handle immigration summary phone input with formatting like immigrant form
  const handleImmigrationSummaryPhoneInput = (e) => {
    const value = e.target.value;
    // Remove all non-digit characters
    let digitsOnly = value.replace(/\D/g, '');
    // Limit to 10 digits
    digitsOnly = digitsOnly.slice(0, 10);

    // Format as XXX-XXX-XXXX while typing
    let formattedValue = digitsOnly;
    if (digitsOnly.length >= 6) {
      formattedValue = digitsOnly.slice(0, 3) + '-' + digitsOnly.slice(3, 6) + '-' + digitsOnly.slice(6, 10);
    } else if (digitsOnly.length >= 3) {
      formattedValue = digitsOnly.slice(0, 3) + '-' + digitsOnly.slice(3);
    }

    setFDLEImmigrationSummaryConfig(prev => ({
      ...prev,
      submitterPhone: formattedValue
    }));
  };



  // Actually perform the FDLE export with the collected configuration
  const performFDLEExport = async () => {
    // Validate required fields (removed detentionFacilityName since we export all facilities)
    if (!fdleExportMonth || !fdleExportConfig.agencyORI || !fdleExportConfig.submitterTitle ||
        !fdleExportConfig.submitterFirstName || !fdleExportConfig.submitterLastName ||
        !fdleExportConfig.submitterIdNumber || !fdleExportConfig.submitterPhone ||
        !fdleExportConfig.submitterEmail) {
      showError('Please fill in all required fields before exporting.');
      return;
    }

    // Validate phone number is exactly 10 digits
    const phoneDigits = fdleExportConfig.submitterPhone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      showError('Phone number must be exactly 10 digits.');
      return;
    }

    // Format the phone number before sending
    const formattedConfig = {
      ...fdleExportConfig,
      submitterPhone: formatPhoneNumber(fdleExportConfig.submitterPhone)
    };

    setExportLoading(true);
    try {


      const token = localStorage.getItem('idToken');
      const apiUrl = `${API_URL}/detention/monthly-export/${fdleExportMonth}?format=fdle`;



      const response = await axios.post(apiUrl, {
        fdleConfig: formattedConfig
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Important for CSV files
      });

      // Handle CSV download
      const blob = new Blob([response.data], {
        type: 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fdle-form-898-entries-${fdleExportMonth}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(` FDLE detention data for ${fdleExportMonth} exported successfully as CSV! Export includes all facilities in the system.`);
      setShowFDLEExportModal(false);
    } catch (error) {
      console.error('Error exporting FDLE detention data:', error);
      const detail = await describeExportError(error);
      showError(`Failed to export FDLE detention data for ${fdleExportMonth}${detail ? `: ${detail}` : '. Please try again.'}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Actually perform the FDLE immigration summary export with the collected configuration
  const performFDLEImmigrationSummaryExport = async () => {
    // Validate required fields
    if (!fdleImmigrationSummaryMonth || !fdleImmigrationSummaryConfig.agencyORI ||
        !fdleImmigrationSummaryConfig.agencyName || !fdleImmigrationSummaryConfig.numberOfDIOs ||
        !fdleImmigrationSummaryConfig.numberOfInvestigations || !fdleImmigrationSummaryConfig.numberOfArrestInvestigations ||
        !fdleImmigrationSummaryConfig.numberOfCivilOnlyArrests || !fdleImmigrationSummaryConfig.submitterTitle ||
        !fdleImmigrationSummaryConfig.submitterFirstName || !fdleImmigrationSummaryConfig.submitterLastName ||
        !fdleImmigrationSummaryConfig.submitterIdNumber || !fdleImmigrationSummaryConfig.submitterPhone ||
        !fdleImmigrationSummaryConfig.submitterEmail) {
      showError('Please fill in all required fields before exporting.');
      return;
    }

    // Validate phone number is exactly 10 digits
    const phoneDigits = fdleImmigrationSummaryConfig.submitterPhone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      showError('Phone number must be exactly 10 digits.');
      return;
    }

    // Format the phone number before sending
    const formattedConfig = {
      ...fdleImmigrationSummaryConfig,
      submitterPhone: formatPhoneNumber(fdleImmigrationSummaryConfig.submitterPhone)
    };

    setExportLoading(true);
    try {
      const token = localStorage.getItem('idToken');
      const apiUrl = `${API_URL}/immigrants/monthly-summary/${fdleImmigrationSummaryMonth}?format=fdle`;

      const response = await axios.post(apiUrl, {
        fdleConfig: formattedConfig
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob' // Important for CSV files
      });

      // Handle CSV download
      const blob = new Blob([response.data], {
        type: 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form-896-entries-${fdleImmigrationSummaryMonth}.csv`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(` FDLE immigration summary for ${fdleImmigrationSummaryMonth} exported successfully!`);
      setShowFDLEImmigrationSummaryModal(false);

      // Reset configuration to empty values after successful export
      setFDLEImmigrationSummaryConfig({
        agencyORI: '',
        agencyName: '',
        hasDIOs: 'Yes',
        numberOfDIOs: '0',
        numberOfInvestigations: '',
        numberOfArrestInvestigations: '',
        numberOfCivilOnlyArrests: '',
        submitterTitle: '',
        submitterFirstName: '',
        submitterLastName: '',
        submitterIdNumber: '',
        submitterPhone: '',
        submitterEmail: ''
      });
    } catch (error) {
      console.error('Error exporting FDLE immigration summary:', error);
      const detail = await describeExportError(error);
      showError(`Failed to export FDLE immigration summary for ${fdleImmigrationSummaryMonth}${detail ? `: ${detail}` : '. Please try again.'}`);
    } finally {
      setExportLoading(false);
    }
  };



  return (
    <div className="monthlySummary">
      <div className="summaryHeader">
        <div className="summaryHeaderLeft">
          <div className="viewModeToggle">
            <button
              className={`toggleBtn ${viewMode === 'detention' ? 'active' : ''}`}
              onClick={() => setViewMode('detention')}
            >
               Detention Data
            </button>
            <button
              className={`toggleBtn ${viewMode === 'immigration' ? 'active' : ''}`}
              onClick={() => setViewMode('immigration')}
            >
              Alien Contacts
            </button>
          </div>
        </div>
        <div className="summaryHeaderActions">
          <div className="monthSelector">
            <label>Select Report Month:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="modern-input"
            >
              <option value="">Select a month...</option>
              {availableMonths.map((month) => (
                <option key={month.month_key} value={month.month_key}>
                  {month.month_label.trim()}
                </option>
              ))}
            </select>
          </div>

          <div className="facilitySelector">
            <label>Select Facility (Optional):</label>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="modern-input"
              title="Filter statistics by specific facility. Leave blank to see all facilities combined."
            >
              <option value="">All Facilities</option>
              {availableFacilities.map((facility) => (
                <option key={facility} value={facility}>
                  {facility}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="summaryActionsRow">
        <div className="summaryActionsLeft">
          {summaryData && (
            <button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="btn btn-primary printPdfBtn"
              title="Download PDF Report"
            >
              {generatingPdf ? (
                <>
                  <span className="spinner"></span>
                  Generating PDF...
                </>
              ) : (
                <>
                   Print PDF
                </>
              )}
            </button>
          )}

          <button
            onClick={() => {
              setExportSelectedMonth('');
              setShowExportModal(true);
            }}
            disabled={exportLoading}
            className="btn btn-secondary exportBtn"
            title="Export Data"
          >
            {exportLoading ? (
              <>
                <span className="spinner"></span>
                Exporting...
              </>
            ) : (
              <>
                Export Data
              </>
            )}
          </button>

          <button
            onClick={() => {
              setDIOAnalysisSelectedMonth('');
              setShowDIOAnalysisModal(true);
            }}
            disabled={exportLoading}
            className="btn btn-success exportBtn"
            style={{ marginLeft: '10px' }}
            title="DIO Analysis Report - Shows which contacts were DIO vs non-DIO investigations"
          >
            {exportLoading ? (
              <>
                <span className="spinner"></span>
                Exporting...
              </>
            ) : (
              <>
                DIO Analysis
              </>
            )}
          </button>

          <button
            onClick={() => {
              setTFMArrestSelectedMonth('');
              setShowTFMArrestModal(true);
            }}
            disabled={exportLoading}
            className="btn btn-info exportBtn"
            style={{ marginLeft: '10px' }}
            title="TFM Arrest Export - Export records where DIO = Yes AND Non-Criminal Arrest = Yes with TFM Arrest Sheet information"
          >
            {exportLoading ? (
              <>
                <span className="spinner"></span>
                Exporting...
              </>
            ) : (
              <>
                TFM Arrest Export
              </>
            )}
          </button>
        </div>

        <div className="summaryActionsRight">
          {/* Submission buttons removed - only export functionality remains */}
        </div>
      </div>





      {/* FDLE Form Modal */}
      {showFDLEFormModal && fdleFormData && (
        <div className="fdle-modal-overlay">
          <div className="fdle-modal" style={{ maxWidth: '1000px' }}>
            <div style={{
              backgroundColor: 'var(--primary-color)',
              background: 'var(--primary-color)',
              color: 'white',
              padding: '20px',
              borderRadius: '8px 8px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #ddd'
            }}>
              <div style={{ color: 'white' }}>
                <h2 style={{
                  color: 'white',
                  margin: '0',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}>Detention Facility Immigration Summary Monthly Data Collection</h2>
              </div>
              <button
                className="fdle-close-btn"
                onClick={() => setShowFDLEFormModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="fdle-modal-body">
              <div className="fdle-form-container" style={{ padding: '20px' }}>
                <div style={{
                  marginBottom: '25px',
                  padding: '15px',
                  background: '#e8f4fd',
                  borderRadius: '6px',
                  borderLeft: '4px solid var(--primary-color)'
                }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#333' }}>
                    County detention facilities must use this form to submit summary data to the State Board of Immigration.
                  </p>
                  <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                    Reports are due by the 15th of each month and must reflect data from the previous calendar month.
                  </p>
                </div>

                {/* FDLE Form Fields */}
                <div className="fdle-form-fields">

                  {/* Agency ORI Number - el-4 */}
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <label htmlFor="el-4" className="form-label">
                        <strong>Agency ORI Number</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-4"
                        className="modern-input"
                        placeholder="Numbers only, no letters - Do not put FL"
                        value={fdleFormData.agencyOriNumber || ''}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            agencyOriNumber: value
                          });
                        }}
                        onKeyDown={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.agencyOriNumber || fdleFormData.agencyOriNumber === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        Enter numbers only, do not include "FL" prefix
                      </small>
                    </div>

                    {/* Date Reporting - el-5 */}
                    <div className="form-group col-md-3">
                      <label htmlFor="el-5" className="form-label">
                        <strong>Date Reporting</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-5"
                        className="modern-input"
                        placeholder="MM/DD/YYYY"
                        value={fdleFormData.dateReporting || ''}
                        onChange={(e) => {
                          // Format as MM/DD/YYYY with proper year handling
                          let value = e.target.value.replace(/[^0-9]/g, '');

                          // Limit to 8 digits (MMDDYYYY)
                          if (value.length > 8) {
                            value = value.substring(0, 8);
                          }

                          // Add slashes at appropriate positions
                          if (value.length >= 2) {
                            value = value.substring(0, 2) + '/' + value.substring(2);
                          }
                          if (value.length >= 5) {
                            value = value.substring(0, 5) + '/' + value.substring(5);
                          }

                          // Ensure we don't truncate the year
                          if (value.length > 10) {
                            value = value.substring(0, 10);
                          }

                          console.log('📅 Date input value:', value); // Debug log

                          setFDLEFormData({
                            ...fdleFormData,
                            dateReporting: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Only allow numbers and prevent more than 10 characters (MM/DD/YYYY)
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        maxLength="10"
                        required
                        style={{
                          marginTop: '5px',
                          textAlign: 'center',
                          borderColor: (!fdleFormData.dateReporting || fdleFormData.dateReporting === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>
                  </div>

                  {/* Detention Facility Reporting - el-6 */}
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <label htmlFor="el-6" className="form-label">
                        <strong>Detention Facility Reporting</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <select
                        id="el-6"
                        className="modern-input"
                        value={fdleFormData.detentionFacilityReporting || ''}
                        onChange={(e) => setFDLEFormData({
                          ...fdleFormData,
                          detentionFacilityReporting: e.target.value
                        })}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.detentionFacilityReporting || fdleFormData.detentionFacilityReporting === '') ? '#dc3545' : '#ced4da'
                        }}
                      >
                        <option value="">Select Detention Facility</option>
                        <option value="Alachua County Jail">Alachua County Jail</option>
                        <option value="Baker County Sheriff's Office">Baker County Sheriff's Office</option>
                        <option value="Bay County Sheriff's Office Jail">Bay County Sheriff's Office Jail</option>
                        <option value="Bradford County Jail">Bradford County Jail</option>
                        <option value="Brevard County Jail Complex">Brevard County Jail Complex</option>
                        <option value="Broward County Sherrif's Office - Joseph V. Conte Facility">Broward County Sherrif's Office - Joseph V. Conte Facility</option>
                        <option value="Broward County Sherrif's Office - Main Jail">Broward County Sherrif's Office - Main Jail</option>
                        <option value="Broward County Sherrif's Office - North Broward Bureau">Broward County Sherrif's Office - North Broward Bureau</option>
                        <option value="Broward County Sherrif's Office - Paul Rein Detention Facility">Broward County Sherrif's Office - Paul Rein Detention Facility</option>
                        <option value="Calhoun County Jail">Calhoun County Jail</option>
                        <option value="Charlotte County Sheriff's Office Jail">Charlotte County Sheriff's Office Jail</option>
                        <option value="Clay County Sheriff's Office Jail/Detention">Clay County Sheriff's Office Jail/Detention</option>
                        <option value="Collier County Sheriff's Office - Immokalee Jail Center">Collier County Sheriff's Office - Immokalee Jail Center</option>
                        <option value="Collier County Sheriff's Office - Naples Jail Center">Collier County Sheriff's Office - Naples Jail Center</option>
                        <option value="Columbia County Detention Facility">Columbia County Detention Facility</option>
                        <option value="Citrus County Detention Facility">Citrus County Detention Facility</option>
                        <option value="DeSoto County Jail">DeSoto County Jail</option>
                        <option value="Dixie County Jail">Dixie County Jail</option>
                        <option value="Escambia County Corrections">Escambia County Corrections</option>
                        <option value="Flagler County Sheriff's Office - Sheriff Perry Hall Inmate Detention Facility">Flagler County Sheriff's Office - Sheriff Perry Hall Inmate Detention Facility</option>
                        <option value="Franklin County Jail">Franklin County Jail</option>
                        <option value="Gadsden County Detention Facility">Gadsden County Detention Facility</option>
                        <option value="Gilchrist County Jail">Gilchrist County Jail</option>
                        <option value="Glades County Jail Operations Center">Glades County Jail Operations Center</option>
                        <option value="Gulf County Jail">Gulf County Jail</option>
                        <option value="Hamilton County Jail">Hamilton County Jail</option>
                        <option value="Hardee County Sheriff's Office">Hardee County Sheriff's Office</option>
                        <option value="Hendry County Sheriff's Office">Hendry County Sheriff's Office</option>
                        <option value="Hernando County Detention Center">Hernando County Detention Center</option>
                        <option value="Highlands County Sheriff's Office Detention Facility">Highlands County Sheriff's Office Detention Facility</option>
                        <option value="Hillsborough County Sheriff's Office - Falkenburg Road Jail">Hillsborough County Sheriff's Office - Falkenburg Road Jail</option>
                        <option value="Hillsborough County Sheriff's Office - Orient Road Jail">Hillsborough County Sheriff's Office - Orient Road Jail</option>
                        <option value="Holmes County Jail">Holmes County Jail</option>
                        <option value="Indian River County Jail">Indian River County Jail</option>
                        <option value="Jackson County Correctional Facility">Jackson County Correctional Facility</option>
                        <option value="Jacksonville Sheriff's Office - Community Transition Center">Jacksonville Sheriff's Office - Community Transition Center</option>
                        <option value="Jacksonville Sheriff's Office - James I. Montgomery Correctional Center">Jacksonville Sheriff's Office - James I. Montgomery Correctional Center</option>
                        <option value="Jacksonville Sheriff's Office - John E. Goode Pre-trial Detention Facility">Jacksonville Sheriff's Office - John E. Goode Pre-trial Detention Facility</option>
                        <option value="Jefferson County Jail">Jefferson County Jail</option>
                        <option value="Lafayette County Jail">Lafayette County Jail</option>
                        <option value="Lake County Detention Center">Lake County Detention Center</option>
                        <option value="Lee County Sheriff's Office - Core Facility / CPU Compound">Lee County Sheriff's Office - Core Facility / CPU Compound</option>
                        <option value="Lee County Sheriff's Office - Main Jail">Lee County Sheriff's Office - Main Jail</option>
                        <option value="Leon County Detention Facility">Leon County Detention Facility</option>
                        <option value="Levy County Detention Center">Levy County Detention Center</option>
                        <option value="Liberty County Jail">Liberty County Jail</option>
                        <option value="Madison County Jail">Madison County Jail</option>
                        <option value="Manatee County Correctional Complex - Annex">Manatee County Correctional Complex - Annex</option>
                        <option value="Manatee County Correctional Complex - Central Jail">Manatee County Correctional Complex - Central Jail</option>
                        <option value="Marion County Jail">Marion County Jail</option>
                        <option value="Martin County Corrections Department">Martin County Corrections Department</option>
                        <option value="Metro West Detention Center">Metro West Detention Center</option>
                        <option value="Pre-Trial Detention Center">Pre-Trial Detention Center</option>
                        <option value="Turner Guilford Knight Correctional Center">Turner Guilford Knight Correctional Center</option>
                        <option value="Miami Dade County Boot Camp">Miami Dade County Boot Camp</option>
                        <option value="Monroe County Sheriff's Office, Detention Bureau">Monroe County Sheriff's Office, Detention Bureau</option>
                        <option value="Nassau County Detention Facility">Nassau County Detention Facility</option>
                        <option value="Okaloosa County Department of Corrections">Okaloosa County Department of Corrections</option>
                        <option value="Okeechobee Coutny Sheriff's Office Jail">Okeechobee Coutny Sheriff's Office Jail</option>
                        <option value="Orange County Corrections Department">Orange County Corrections Department</option>
                        <option value="Osceola County Corrections Facility">Osceola County Corrections Facility</option>
                        <option value="Palm Beach County Sheriff's Office - Main Detention Center">Palm Beach County Sheriff's Office - Main Detention Center</option>
                        <option value="Palm Beach County Sheriff's Office - West Detention Center">Palm Beach County Sheriff's Office - West Detention Center</option>
                        <option value="Pasco County Detention Center">Pasco County Detention Center</option>
                        <option value="Pinellas County Jail">Pinellas County Jail</option>
                        <option value="Polk County Sheriff's Office - Central County Jail">Polk County Sheriff's Office - Central County Jail</option>
                        <option value="Polk County Sheriff's Office - South County Jail">Polk County Sheriff's Office - South County Jail</option>
                        <option value="Putnam County Sheriff's Office">Putnam County Sheriff's Office</option>
                        <option value="Santa Rosa County Sheriff's Office - Main">Santa Rosa County Sheriff's Office - Main</option>
                        <option value="Santa Rosa County Sheriff's Office - Satellite">Santa Rosa County Sheriff's Office - Satellite</option>
                        <option value="Sarasota County Correctional Facility">Sarasota County Correctional Facility</option>
                        <option value="Seminole County Sheriff's Office - John E. Polk Correctional Facility">Seminole County Sheriff's Office - John E. Polk Correctional Facility</option>
                        <option value="Saint Lucie County Jail">Saint Lucie County Jail</option>
                        <option value="St. Johns County Detention Center">St. Johns County Detention Center</option>
                        <option value="Sumter County Jail">Sumter County Jail</option>
                        <option value="Suwanee County Jail">Suwanee County Jail</option>
                        <option value="Taylor County Jail">Taylor County Jail</option>
                        <option value="Test County Detention Facility">Test County Detention Facility</option>
                        <option value="Union County Jail">Union County Jail</option>
                        <option value="Volusia County Branch Jail">Volusia County Branch Jail</option>
                        <option value="Volusia County Correctional Facility">Volusia County Correctional Facility</option>
                        <option value="Wakulla County Sheriff's Office Detention Facility">Wakulla County Sheriff's Office Detention Facility</option>
                        <option value="Walton County Jail">Walton County Jail</option>
                        <option value="Washington County Jail">Washington County Jail</option>
                      </select>
                    </div>
                  </div>

                  {/* Month Reporting - el-7 and Year Reporting - el-8 */}
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label htmlFor="el-7" className="form-label">
                        <strong>Month Reporting</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <select
                        id="el-7"
                        className="modern-input"
                        value={fdleFormData.monthReporting || ''}
                        onChange={(e) => setFDLEFormData({
                          ...fdleFormData,
                          monthReporting: e.target.value
                        })}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.monthReporting || fdleFormData.monthReporting === '') ? '#dc3545' : '#ced4da'
                        }}
                      >
                        <option value="">Select Month</option>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                    </div>

                    <div className="form-group col-md-2">
                      <label htmlFor="el-8" className="form-label">
                        <strong>Year Reporting</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <select
                        id="el-8"
                        className="modern-input"
                        value={fdleFormData.yearReporting || ''}
                        onChange={(e) => setFDLEFormData({
                          ...fdleFormData,
                          yearReporting: e.target.value
                        })}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.yearReporting || fdleFormData.yearReporting === '') ? '#dc3545' : '#ced4da'
                        }}
                      >
                        <option value="">Select Year</option>
                        <option value="2025">2025</option>
                        <option value="2026">2026</option>
                        <option value="2027">2027</option>
                      </select>
                    </div>
                  </div>

                  {/* Immigration Detainer Forms - el-10 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-10" className="form-label">
                        <strong>Number of immigration detainer forms (I-247) received from ICE during the reporting month.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-10"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.immigrationDetainerForms || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            immigrationDetainerForms: value
                          });
                        }}
                        onKeyDown={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.immigrationDetainerForms && fdleFormData.immigrationDetainerForms !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records where ICE detainer was received
                      </small>
                    </div>
                  </div>

                  {/* Immigration Detainer Forms with I-200s - el-11 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-11" className="form-label">
                        <strong>Number of immigration detainer forms (I-247) received from ICE during the reporting month accompanied by I-200s.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-11"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.immigrationDetainerI200 || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            immigrationDetainerI200: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.immigrationDetainerI200 && fdleFormData.immigrationDetainerI200 !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records accompanied by I-200 forms
                      </small>
                    </div>
                  </div>

                  {/* Immigration Detainer Forms with I-205s - el-12 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-12" className="form-label">
                        <strong>Number of immigration detainer forms (I-247) received from ICE during the reporting month accompanied by I-205s.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-12"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.immigrationDetainerI205 || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            immigrationDetainerI205: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.immigrationDetainerI205 && fdleFormData.immigrationDetainerI205 !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records accompanied by I-205 forms
                      </small>
                    </div>
                  </div>

                  {/* Released Without ICE Custody - el-13 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-13" className="form-label">
                        <strong>Number of immigration detainers lodged and received from ICE and then cancelled by ICE resulting in the person released from the detention facility without being taken into ICE custody.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-13"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.releasedIceTookCustody || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            releasedIceTookCustody: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.releasedIceTookCustody && fdleFormData.releasedIceTookCustody !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records released without ICE custody
                      </small>
                    </div>
                  </div>

                  {/* Released Violent Crime - el-14 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-14" className="form-label">
                        <strong>Number of individuals who were charged with a violent crime who had an immigration detainer lodged and received from ICE and then cancelled by ICE resulting in the person released from the detention facility without being taken into ICE custody.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-14"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.releasedViolentCrime || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            releasedViolentCrime: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.releasedViolentCrime && fdleFormData.releasedViolentCrime !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records released due to 48hr timeout
                      </small>
                    </div>
                  </div>

                  {/* ICE Failed 48hr Pickup - el-15 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-15" className="form-label">
                        <strong>Number of persons for whom the detention facility received ICE immigration detainers for, and ICE failed to take custody of the person from the detention facility within the required 48-hour period and the facility had to release the person from custody.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-15"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.iceFailed48hrPickup || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            iceFailed48hrPickup: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.iceFailed48hrPickup && fdleFormData.iceFailed48hrPickup !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records where ICE failed 48hr pickup
                      </small>
                    </div>
                  </div>

                  {/* Released Before ICE Detainer - el-16 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-16" className="form-label">
                        <strong>Number of persons the detention facility released from custody on state law charges, and after the person was released, the detention facility recieved an immigration detainer from ICE.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-16"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.releasedBeforeIceDetainer || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            releasedBeforeIceDetainer: value
                          });
                        }}
                        onKeyDown={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.releasedBeforeIceDetainer && fdleFormData.releasedBeforeIceDetainer !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records released before ICE detainer received
                      </small>
                    </div>
                  </div>

                  {/* JEM Detainer Before ICE Match - el-17 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-17" className="form-label">
                        <strong>If the detention facility participates in the Jail Enforcement Model program, report the number of persons for whom it lodged immigration detainers on before ICE made an independent biometric or biographic match through the normal booking process.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-17"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.jemDetainerBeforeIceMatch || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            jemDetainerBeforeIceMatch: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.jemDetainerBeforeIceMatch && fdleFormData.jemDetainerBeforeIceMatch !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from JEM detainer records before ICE match
                      </small>
                    </div>
                  </div>

                  {/* Bed Days Incurred - el-18 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-18" className="form-label">
                        <strong>How many bed days the jail incurred for housing unauthorized aliens from the time their state law charges were resolved until they were released from jail or released to ICE custody.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-18"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.bedDaysIncurred || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            bedDaysIncurred: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.bedDaysIncurred && fdleFormData.bedDaysIncurred !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from the sum of bed days incurred for the selected month
                      </small>
                    </div>
                  </div>

                  {/* Transported to ICE Office - el-19 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-19" className="form-label">
                        <strong>Number of persons the detention facility transported from the detention facility to an ICE Office upon conclusion of the 48-hour immigration detainer period in lieu of ICE taking custody of the person at the detention facility.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="number"
                        id="el-19"
                        className="modern-input"
                        placeholder="Numbers only, no commas"
                        value={fdleFormData.transportedToIceOffice || ''}
                        onChange={(e) => {
                          // Only allow numbers
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setFDLEFormData({
                            ...fdleFormData,
                            transportedToIceOffice: value
                          });
                        }}
                        onKeyDown={(e) => {
                          // Prevent non-numeric characters from being typed
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.transportedToIceOffice && fdleFormData.transportedToIceOffice !== 0) ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically populated from detention records transported to ICE office
                      </small>
                    </div>
                  </div>

                  {/* Housing Cost Reimbursement - el-20 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-20" className="form-label">
                        <strong>The amount of unauthorized alien housing cost reimbursement it received from ICE during the reporting month.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-20"
                        className="modern-input"
                        placeholder="No dollar signs, only commas and periods. Example: 15,445.87"
                        value={fdleFormData.housingCostReimbursement || ''}
                        onChange={(e) => {
                          // Allow numbers, commas, and periods for currency formatting
                          let value = e.target.value.replace(/[^0-9.,]/g, '');

                          // Ensure only one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          // Limit to 2 decimal places
                          if (parts[1] && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }

                          setFDLEFormData({
                            ...fdleFormData,
                            housingCostReimbursement: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Allow numbers, commas, periods, backspace, delete, tab
                          if (!/[0-9.,]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.housingCostReimbursement || fdleFormData.housingCostReimbursement === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This field is automatically calculated: Count of housing cost reimbursement received × $50.00
                      </small>
                    </div>
                  </div>

                  {/* Detention Facility Reimbursed - el-21 */}
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <label htmlFor="el-21" className="form-label">
                        <strong>The detention facility is reimbursed:</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <select
                        id="el-21"
                        className="modern-input"
                        value={fdleFormData.detentionFacilityReimbursed || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            detentionFacilityReimbursed: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.detentionFacilityReimbursed || fdleFormData.detentionFacilityReimbursed === '') ? '#dc3545' : '#ced4da'
                        }}
                      >
                        <option value="">Select reimbursement type</option>
                        <option value="Per Day">Per Day</option>
                        <option value="Per Stay">Per Stay</option>
                      </select>
                    </div>
                  </div>

                  {/* ICE Reimbursement Rate - el-22 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-22" className="form-label">
                        <strong>The reimbursement rate from ICE for this detention facility.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-22"
                        className="modern-input"
                        placeholder="No dollar signs, only commas and periods. Example: 45.89"
                        value={fdleFormData.iceReimbursementRate || ''}
                        onChange={(e) => {
                          // Allow numbers, commas, and periods for currency formatting
                          let value = e.target.value.replace(/[^0-9.,]/g, '');

                          // Ensure only one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          // Limit to 2 decimal places
                          if (parts[1] && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }

                          setFDLEFormData({
                            ...fdleFormData,
                            iceReimbursementRate: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Allow numbers, commas, periods, backspace, delete, tab
                          if (!/[0-9.,]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.iceReimbursementRate || fdleFormData.iceReimbursementRate === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        Standard ICE reimbursement rate is $50.00 per day per detainee
                      </small>
                    </div>
                  </div>

                  {/* Daily Housing Cost - el-23 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-23" className="form-label">
                        <strong>The detention facility housing cost per day per inmate.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-23"
                        className="modern-input"
                        placeholder="No dollar signs, only commas and periods. Example: 15,445.87"
                        value={fdleFormData.dailyHousingCost || ''}
                        onChange={(e) => {
                          // Allow numbers, commas, and periods for currency formatting
                          let value = e.target.value.replace(/[^0-9.,]/g, '');

                          // Ensure only one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          // Limit to 2 decimal places
                          if (parts[1] && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }

                          setFDLEFormData({
                            ...fdleFormData,
                            dailyHousingCost: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Allow numbers, commas, periods, backspace, delete, tab
                          if (!/[0-9.,]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.dailyHousingCost || fdleFormData.dailyHousingCost === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        Auto-calculated: Bed days × $80.86 (0 if bed days = 0)
                      </small>
                    </div>
                  </div>

                  {/* Housing Cost Difference - el-24 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-24" className="form-label">
                        <strong>The difference between the daily detention facility housing cost for an unauthorized alien and the ICE reimbursable rate.</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-24"
                        className="modern-input"
                        placeholder="No dollar signs, only commas and periods. Example: 23.56"
                        value={fdleFormData.housingCostDifference || ''}
                        onChange={(e) => {
                          // Allow numbers, commas, and periods for currency formatting
                          let value = e.target.value.replace(/[^0-9.,]/g, '');

                          // Ensure only one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            value = parts[0] + '.' + parts.slice(1).join('');
                          }

                          // Limit to 2 decimal places
                          if (parts[1] && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }

                          setFDLEFormData({
                            ...fdleFormData,
                            housingCostDifference: value
                          });
                        }}
                        onKeyPress={(e) => {
                          // Allow numbers, commas, periods, backspace, delete, tab
                          if (!/[0-9.,]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.housingCostDifference || fdleFormData.housingCostDifference === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                        This is calculated as: Daily Housing Cost - ICE Reimbursement Rate
                      </small>
                    </div>
                  </div>

                  {/* Offense Classification File - el-25 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <label htmlFor="el-25" className="form-label">
                        <strong>For each individual against whom ICE has lodged an immigration detainer, report the highest state law offense classification. Please submit an Excel file that includes the following information for each person: Last Name, First Name, Date of Birth, and Highest State Law Offense Classification.</strong>
                      </label>

                      <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={async () => {
                            try {
                              console.log(' Testing basic Excel generation...');
                              const response = await fetch('/api/immigrants/test-excel', {
                                method: 'GET',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                              });

                              if (response.ok) {
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = 'test.xlsx';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                alert(' Test Excel file downloaded!');
                              } else {
                                alert(' Test failed');
                              }
                            } catch (error) {
                              console.error('Test error:', error);
                              alert(' Test error: ' + error.message);
                            }
                          }}
                          style={{ marginRight: '10px' }}
                        >
                           Test Excel
                        </button>

                        <button
                          type="button"
                          className="btn btn-info btn-sm"
                          onClick={async (event) => {
                            try {
                              const reportMonth = selectedMonth; // Format: YYYY-MM

                              // Show loading state
                              const button = event.target;
                              const originalText = button.textContent;
                              button.textContent = '⏳ Generating...';
                              button.disabled = true;

                              const response = await fetch(`/api/immigrants/generate-fdle-excel/${reportMonth}`, {
                                method: 'GET',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                }
                              });

                              if (response.ok) {
                                // Check if response is actually an Excel file
                                const contentType = response.headers.get('content-type');
                                console.log('Response content-type:', contentType);
                                console.log('Response headers:', [...response.headers.entries()]);
                                console.log('Response status:', response.status);
                                console.log('Response URL:', response.url);

                                // Temporarily skip content type check for debugging
                                // if (!contentType || (!contentType.includes('spreadsheetml') && !contentType.includes('application/octet-stream'))) {
                                //   console.error('Invalid content type received:', contentType);
                                //   throw new Error(`Invalid file format received from server. Content-Type: ${contentType}`);
                                // }

                                // Create blob from response with correct MIME type
                                const blob = await response.blob();

                                // Validate blob size
                                if (blob.size === 0) {
                                  throw new Error('Received empty file from server');
                                }

                                // Create download link
                                const url = window.URL.createObjectURL(new Blob([blob], {
                                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                }));
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `FDLE_ICE_Detainer_Offenses_${reportMonth}.xlsx`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);

                                alert(' Excel file generated and downloaded successfully!');
                              } else {
                                const errorData = await response.json();
                                alert(` Error generating Excel file: ${errorData.message}`);
                              }

                              // Restore button state
                              button.textContent = originalText;
                              button.disabled = false;

                            } catch (error) {
                              console.error('Error generating Excel file:', error);
                              alert(` Failed to generate Excel file: ${error.message}`);

                              // Restore button state
                              const button = event.target;
                              button.textContent = ' Auto-Generate Excel File';
                              button.disabled = false;
                            }
                          }}
                          style={{ marginRight: '10px' }}
                        >
                           Auto-Generate Excel File
                        </button>
                        <small style={{ color: '#666', fontSize: '11px' }}>
                          Click to automatically generate Excel file from your detention records
                        </small>
                      </div>

                      <div className="input-group">
                        <input
                          type="file"
                          id="el-25"
                          className="form-control"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setFDLEFormData({
                              ...fdleFormData,
                              offenseClassificationFile: file
                            });
                          }}
                        />
                      </div>

                      <small className="form-help-text" style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', marginTop: '5px' }}>
                        Optional: Upload Excel file with Last Name, First Name, Date of Birth, and Highest State Law Offense Classification. Or use the auto-generate button above.
                      </small>
                    </div>
                  </div>

                  {/* Certification Checkbox - el-26 */}
                  <div className="form-row">
                    <div className="form-group col-md-12">
                      <div className="form-check" style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <input
                          type="checkbox"
                          id="el-26"
                          className="form-check-input"
                          checked={fdleFormData.certificationConfirmed || false}
                          onChange={(e) => {
                            setFDLEFormData({
                              ...fdleFormData,
                              certificationConfirmed: e.target.checked
                            });
                          }}
                          required
                          style={{
                            borderColor: !fdleFormData.certificationConfirmed ? '#dc3545' : '#ced4da'
                          }}
                        />
                        <label htmlFor="el-26" className="form-check-label">
                          <strong>I certify the information is submitted is true and correct.</strong>
                          <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Submitter Information Section */}
                  <div className="form-row">
                    <div className="col-12">
                      <h5 style={{ marginTop: '30px', marginBottom: '20px', color: '#495057', borderBottom: '2px solid #dee2e6', paddingBottom: '10px' }}>
                        Submitter Information
                      </h5>
                    </div>
                  </div>

                  {/* Submitter's Title - el-28 */}
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label htmlFor="el-28" className="form-label">
                        <strong>Submitter's Title</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-28"
                        className="modern-input"
                        maxLength="255"
                        value={fdleFormData.submitterTitle || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterTitle: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterTitle || fdleFormData.submitterTitle === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>

                    {/* Submitter's First Name - el-29 */}
                    <div className="form-group col-md-4">
                      <label htmlFor="el-29" className="form-label">
                        <strong>Submitter's First Name</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-29"
                        className="modern-input"
                        maxLength="255"
                        value={fdleFormData.submitterFirstName || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterFirstName: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterFirstName || fdleFormData.submitterFirstName === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>

                    {/* Submitter's Last Name - el-30 */}
                    <div className="form-group col-md-4">
                      <label htmlFor="el-30" className="form-label">
                        <strong>Submitter's Last Name</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-30"
                        className="modern-input"
                        maxLength="255"
                        value={fdleFormData.submitterLastName || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterLastName: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterLastName || fdleFormData.submitterLastName === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>
                  </div>

                  {/* Submitter's ID Number - el-31 */}
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <label htmlFor="el-31" className="form-label">
                        <strong>Submitter's ID Number</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="el-31"
                        className="modern-input"
                        maxLength="255"
                        value={fdleFormData.submitterIdNumber || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterIdNumber: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterIdNumber || fdleFormData.submitterIdNumber === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>

                    {/* Submitter's Phone Number - el-32 */}
                    <div className="form-group col-md-4">
                      <label htmlFor="el-32" className="form-label">
                        <strong>Submitter's Phone Number</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        id="el-32"
                        className="modern-input"
                        placeholder="555-555-5555"
                        value={fdleFormData.submitterPhoneNumber || ''}
                        onChange={(e) => {
                          // Format phone number as user types
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length >= 6) {
                            value = value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
                          } else if (value.length >= 3) {
                            value = value.replace(/(\d{3})(\d{3})/, '$1-$2');
                          }
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterPhoneNumber: value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterPhoneNumber || fdleFormData.submitterPhoneNumber === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>

                    {/* Submitter's Email Address - el-33 */}
                    <div className="form-group col-md-4">
                      <label htmlFor="el-33" className="form-label">
                        <strong>Submitter's Email Address</strong>
                        <span className="required-asterisk" style={{ color: 'red', marginLeft: '4px' }}>*</span>
                      </label>
                      <input
                        type="email"
                        id="el-33"
                        className="modern-input"
                        maxLength="255"
                        value={fdleFormData.submitterEmail || ''}
                        onChange={(e) => {
                          setFDLEFormData({
                            ...fdleFormData,
                            submitterEmail: e.target.value
                          });
                        }}
                        required
                        style={{
                          marginTop: '5px',
                          borderColor: (!fdleFormData.submitterEmail || fdleFormData.submitterEmail === '') ? '#dc3545' : '#ced4da'
                        }}
                      />
                    </div>
                  </div>



                </div>
              </div>
            </div>
            <div className="fdle-modal-footer">
              <button
                className="modern-btn"
                onClick={() => setShowFDLEFormModal(false)}
                style={{ backgroundColor: '#6c757d' }}
              >
                Cancel
              </button>
              {/* FDLE submission functionality removed - only export functionality remains */}
            </div>
          </div>
        </div>
      )}

      {/* FDLE Progress Modal removed - using test form loading instead */}

      {/* FDLE submission functionality removed - only export functionality remains */}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay">
          <div className="modal-content export-modal">
            <div className="modal-header">
              <h3>Export Data</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowExportModal(false)}
                disabled={exportLoading}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="export-options">

                {/* Month Selection Section */}
                <div className="export-month-selection">
                  <h4>Select Export Month</h4>
                  <input
                    type="month"
                    value={exportSelectedMonth}
                    onChange={(e) => setExportSelectedMonth(e.target.value)}
                    className="month-input"
                    disabled={exportLoading}
                  />
                  {exportSelectedMonth && (
                    <button
                      onClick={() => setExportSelectedMonth('')}
                      className="clear-month-btn"
                      disabled={exportLoading}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                {/* Immigrant Records Section */}
                <div className="export-section">
                  <h4>Immigrant Records</h4>
                  <div className="export-format-group">
                    <div className="format-section">
                      <h5>FDLE Individual Records</h5>
                      <div className="export-buttons">
                        <button
                          onClick={() => exportFDLEImmigrationData(exportSelectedMonth)}
                          disabled={exportLoading || !exportSelectedMonth}
                          className="modern-btn"
                          title={!exportSelectedMonth ? 'Please select a month to export FDLE format' : 'Export immigration data as ZIP file containing Excel spreadsheet and photos folder for SFTP upload'}
                        >
                          {exportSelectedMonth ? (
                            <>
                              Export {exportSelectedMonth}
                              <br />
                              (ZIP: Excel + Photos)
                            </>
                          ) : (
                            <>
                              Export Individual Records
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="format-section">
                      <h5>FDLE Monthly Summary CSV</h5>
                      <div className="export-buttons">
                        <button
                          onClick={() => exportFDLEImmigrationSummary(exportSelectedMonth)}
                          disabled={exportLoading || !exportSelectedMonth}
                          className="modern-btn"
                          title={!exportSelectedMonth ? 'Please select a month to export FDLE summary' : 'Export immigration monthly summary in FDLE CSV format'}
                        >
                          {exportSelectedMonth ? (
                            <>
                              Export {exportSelectedMonth}
                              <br />
                              (Monthly Summary)
                            </>
                          ) : (
                            <>
                              Export Monthly Summary
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detention Records Section */}
                <div className="export-section">
                  <h4>Detention Records</h4>
                  <div className="export-format-group">
                    <div className="format-section">
                      <h5>FDLE CSV Format</h5>
                      <div className="export-buttons">
                        <button
                          onClick={() => exportFDLEDetentionData(exportSelectedMonth)}
                          disabled={exportLoading || !exportSelectedMonth}
                          className="modern-btn"
                          title={!exportSelectedMonth ? 'Please select a month to export FDLE format' : 'Export detention data from all facilities in FDLE CSV format'}
                        >
                          {exportSelectedMonth ? (
                            <>
                              Export {exportSelectedMonth}
                              <br />
                              (FDLE CSV)
                            </>
                          ) : (
                            <>
                              Export FDLE CSV
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="format-section">
                      <h5>Highest Offense Classification</h5>
                      <div className="export-buttons">
                        <button
                          onClick={() => exportOffenseClassification(exportSelectedMonth)}
                          disabled={exportLoading || !exportSelectedMonth}
                          className="modern-btn"
                          title={!exportSelectedMonth ? 'Please select a month' : 'Export highest state law offense classification for individuals with ICE detainers'}
                        >
                          {exportSelectedMonth ? (
                            <>
                              Export {exportSelectedMonth}
                              <br />
                              (Offense Classification)
                            </>
                          ) : (
                            <>
                              Export Offense Classification
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {exportLoading && (
                  <div className="export-loading">
                    <div className="loading-spinner"></div>
                    <p>Preparing your export...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* FDLE Export Configuration Modal */}
      {showFDLEExportModal && (
        <div className="modal-overlay">
          <div className="modal-content export-modal">
            <div className="modal-header">
              <h3>FDLE Detention Export Configuration</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowFDLEExportModal(false)}
                disabled={exportLoading}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f4fd', borderRadius: '6px', borderLeft: '4px solid var(--primary-color)' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#333' }}>
                  FDLE Detention Data Export for {fdleExportMonth}
                </p>
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                  This export will include data from all detention facilities in the system for the selected month. Each facility will appear on its own row in the export file. Please fill in the required information below.
                </p>
              </div>

              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="agencyORI">Agency ORI Number *</label>
                  <input
                    type="text"
                    id="agencyORI"
                    value={fdleExportConfig.agencyORI}
                    onChange={(e) => {
                      // Only allow digits, any length
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setFDLEExportConfig(prev => ({ ...prev, agencyORI: value }));
                    }}
                    onKeyDown={(e) => {
                      // Prevent non-numeric characters from being typed
                      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                        e.preventDefault();
                      }
                    }}
                    className="modern-input"
                    placeholder="Enter agency ORI number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="reimbursementType">Reimbursement Type *</label>
                  <select
                    id="reimbursementType"
                    value={fdleExportConfig.reimbursementType}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, reimbursementType: e.target.value }))}
                    className="modern-input"
                    title="Defaults to 'Per Stay' - change if needed"
                    required
                  >
                    <option value="">Select Reimbursement Type</option>
                    <option value="Per Stay">Per Stay</option>
                    <option value="Per Day">Per Day</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Defaults to "Per Stay"
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="submitterTitle">Submitter's Title *</label>
                  <input
                    type="text"
                    id="submitterTitle"
                    value={fdleExportConfig.submitterTitle}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, submitterTitle: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="submitterFirstName">Submitter's First Name *</label>
                  <input
                    type="text"
                    id="submitterFirstName"
                    value={fdleExportConfig.submitterFirstName}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, submitterFirstName: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's first name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="submitterLastName">Submitter's Last Name *</label>
                  <input
                    type="text"
                    id="submitterLastName"
                    value={fdleExportConfig.submitterLastName}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, submitterLastName: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's last name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="submitterIdNumber">Submitter's ID Number *</label>
                  <input
                    type="text"
                    id="submitterIdNumber"
                    value={fdleExportConfig.submitterIdNumber}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, submitterIdNumber: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's ID number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="submitterPhone">Submitter's Phone Number *</label>
                  <input
                    type="tel"
                    id="submitterPhone"
                    value={fdleExportConfig.submitterPhone}
                    onChange={handlePhoneInput}
                    className="modern-input"
                    placeholder="Enter phone number (XXX-XXX-XXXX)"
                    maxLength="12"
                    required
                  />
                </div>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor="submitterEmail">Submitter's Email Address *</label>
                  <input
                    type="email"
                    id="submitterEmail"
                    value={fdleExportConfig.submitterEmail}
                    onChange={(e) => setFDLEExportConfig(prev => ({ ...prev, submitterEmail: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's email address"
                    required
                  />
                </div>
              </div>



              <div className="modal-actions" style={{ marginTop: '25px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowFDLEExportModal(false)}
                  disabled={exportLoading}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={performFDLEExport}
                  disabled={exportLoading}
                  className="modern-btn"
                  style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                >
                  {exportLoading ? 'Generating...' : 'Generate FDLE Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* FDLE Immigration Summary Configuration Modal */}
      {showFDLEImmigrationSummaryModal && (
        <div className="modal-overlay">
          <div className="modal-content export-modal">
            <div className="modal-header">
              <h3>FDLE Immigration Monthly Summary Configuration</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowFDLEImmigrationSummaryModal(false)}
                disabled={exportLoading}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f4fd', borderRadius: '6px', borderLeft: '4px solid var(--primary-color)' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#333' }}>
                  Law Enforcement Agency Immigration Monthly Summary for {fdleImmigrationSummaryMonth}
                </p>
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                  Please fill in the required information below to generate the FDLE monthly summary report.
                </p>
              </div>

              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="summaryAgencyORI">Agency ORI Number *</label>
                  <input
                    type="text"
                    id="summaryAgencyORI"
                    value={fdleImmigrationSummaryConfig.agencyORI}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, agencyORI: e.target.value }))}
                    className="modern-input"
                    placeholder="Numbers only, no letters - Do not put FL"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summaryAgencyName">Law Enforcement Agency Reporting *</label>
                  <AgencyDropdown
                    name="agencyName"
                    value={fdleImmigrationSummaryConfig.agencyName}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, agencyName: e.target.value }))}
                    className="modern-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summaryHasDIOs">Does your agency have task force Designated Immigration Officers (DIOs) under §287(g) of the INA? *</label>
                  <select
                    id="summaryHasDIOs"
                    value={fdleImmigrationSummaryConfig.hasDIOs}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, hasDIOs: e.target.value }))}
                    className="modern-input"
                    required
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="summaryNumberOfDIOs">Number of certified DIOs in your agency. *</label>
                  <input
                    type="number"
                    id="summaryNumberOfDIOs"
                    value={fdleImmigrationSummaryConfig.numberOfDIOs}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, numberOfDIOs: e.target.value }))}
                    className="modern-input"
                    placeholder="e.g., 1"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summaryNumberOfInvestigations">Number of immigration investigations conducted by your DIOs for this reporting month. *</label>
                  <input
                    type="number"
                    id="summaryNumberOfInvestigations"
                    value={fdleImmigrationSummaryConfig.numberOfInvestigations}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, numberOfInvestigations: e.target.value }))}
                    className="modern-input"
                    placeholder="e.g., 5"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summaryNumberOfArrestInvestigations">Number of immigration investigations conducted by your DIOs that resulted in an arrest for this reporting month? *</label>
                  <input
                    type="number"
                    id="summaryNumberOfArrestInvestigations"
                    value={fdleImmigrationSummaryConfig.numberOfArrestInvestigations}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, numberOfArrestInvestigations: e.target.value }))}
                    className="modern-input"
                    placeholder="e.g., 2"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label htmlFor="summaryNumberOfCivilOnlyArrests">Number of arrests of an unauthorized alien on ONLY civil immigration charges with NO accompanying state or local law criminal charges. *</label>
                  <input
                    type="number"
                    id="summaryNumberOfCivilOnlyArrests"
                    value={fdleImmigrationSummaryConfig.numberOfCivilOnlyArrests}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, numberOfCivilOnlyArrests: e.target.value }))}
                    className="modern-input"
                    placeholder="e.g., 1"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterTitle">Submitter's Title *</label>
                  <input
                    type="text"
                    id="summarySubmitterTitle"
                    value={fdleImmigrationSummaryConfig.submitterTitle}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, submitterTitle: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterFirstName">Submitter's First Name *</label>
                  <input
                    type="text"
                    id="summarySubmitterFirstName"
                    value={fdleImmigrationSummaryConfig.submitterFirstName}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, submitterFirstName: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's first name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterLastName">Submitter's Last Name *</label>
                  <input
                    type="text"
                    id="summarySubmitterLastName"
                    value={fdleImmigrationSummaryConfig.submitterLastName}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, submitterLastName: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's last name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterIdNumber">Submitter's ID Number *</label>
                  <input
                    type="text"
                    id="summarySubmitterIdNumber"
                    value={fdleImmigrationSummaryConfig.submitterIdNumber}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, submitterIdNumber: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's ID number"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterPhone">Submitter's Phone Number *</label>
                  <input
                    type="tel"
                    id="summarySubmitterPhone"
                    value={fdleImmigrationSummaryConfig.submitterPhone}
                    onChange={handleImmigrationSummaryPhoneInput}
                    className="modern-input"
                    placeholder="Enter submitter's phone number"
                    maxLength="12"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="summarySubmitterEmail">Submitter's Email Address *</label>
                  <input
                    type="email"
                    id="summarySubmitterEmail"
                    value={fdleImmigrationSummaryConfig.submitterEmail}
                    onChange={(e) => setFDLEImmigrationSummaryConfig(prev => ({ ...prev, submitterEmail: e.target.value }))}
                    className="modern-input"
                    placeholder="Enter submitter's email address"
                    required
                  />
                </div>
              </div>

              <div className="modal-actions" style={{ marginTop: '25px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowFDLEImmigrationSummaryModal(false)}
                  disabled={exportLoading}
                  className="modern-btn"
                  style={{ backgroundColor: '#6c757d', color: 'white' }}
                >
                  Cancel
                </button>
                <button
                  onClick={performFDLEImmigrationSummaryExport}
                  disabled={exportLoading}
                  className="modern-btn"
                  style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                >
                  {exportLoading ? 'Generating...' : 'Generate Summary Export'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}







      {!selectedMonth && (
        <div className="noDataContainer">
          <p>Please select a month from the dropdown above to view the monthly summary report.</p>
        </div>
      )}

      {loading && selectedMonth && (
        <div className="loadingContainer">
          <div className="spinner"></div>
          <p>Loading monthly summary data for {selectedMonth}...</p>
        </div>
      )}

      {!loading && selectedMonth && !summaryData && (
        <div className="noDataContainer">
          <p>No detention data available for the selected month. This shouldn't happen - please try refreshing the page.</p>
        </div>
      )}

      {!loading && summaryData && (
        <div className="summaryContent" ref={printRef}>

          {viewMode === 'detention' && (
            <>
              {/* DETENTION VIEW CONTENT */}

          {/* County Jail Summary */}
          <div className="summarySection">
            <h4>
              County Jail Data Summary
              {selectedFacility && (
                <span style={{ fontSize: '0.9em', color: '#666', marginLeft: '10px' }}>
                  - {selectedFacility}
                </span>
              )}
              {!selectedFacility && (
                <span style={{ fontSize: '0.9em', color: '#666', marginLeft: '10px' }}>
                  - All Facilities
                </span>
              )}
            </h4>
            <div className="summaryGrid">
              <div className="summaryCard">
                <h5>ICE Detainer Activity</h5>
                <div className="statRow">
                  <span>Total Records:</span>
                  <span className="statValue">{summaryData.countyJailSummary.totalRecords}</span>
                </div>
                <div className="statRow">
                  <span>ICE Detainers Received (I-247):</span>
                  <span className="statValue">{summaryData.countyJailSummary.iceDetainersReceived}</span>
                </div>
                <div className="statRow">
                  <span>Accompanied by I-200:</span>
                  <span className="statValue">{summaryData.countyJailSummary.accompaniedByI200}</span>
                </div>
                <div className="statRow">
                  <span>Accompanied by I-205:</span>
                  <span className="statValue">{summaryData.countyJailSummary.accompaniedByI205}</span>
                </div>
                <div className="statRow">
                  <span>Detainers Lodged:</span>
                  <span className="statValue">{summaryData.countyJailSummary.detainersLodged}</span>
                </div>
                <div className="statRow">
                  <span>Detainers Cancelled by ICE:</span>
                  <span className="statValue">{summaryData.countyJailSummary.detainersCancelled}</span>
                </div>
              </div>

              <div className="summaryCard">
                <h5>48-Hour Period & Release Data</h5>
                <div className="statRow">
                  <span>ICE Failed 48hr Pickup:</span>
                  <span className="statValue">{summaryData.countyJailSummary.iceFailedPickup}</span>
                </div>
                <div className="statRow">
                  <span>Released Due to Timeout:</span>
                  <span className="statValue">{summaryData.countyJailSummary.releasedDueToTimeout}</span>
                </div>
                <div className="statRow">
                  <span>Released with Violent Crime:</span>
                  <span className="statValue">{summaryData.countyJailSummary.releasedWithViolentCrime}</span>
                </div>
                <div className="statRow">
                  <span>Released Before ICE Detainer:</span>
                  <span className="statValue">{summaryData.countyJailSummary.releasedBeforeDetainer}</span>
                </div>
                <div className="statRow">
                  <span>JEM Detainers:</span>
                  <span className="statValue">{summaryData.countyJailSummary.jemDetainers}</span>
                </div>
                <div className="statRow">
                  <span>Transported to ICE Office by Detention:</span>
                  <span className="statValue">{summaryData.countyJailSummary.transportedToIce}</span>
                </div>
                <div className="statRow">
                  <span>ICE Took Custody:</span>
                  <span className="statValue">{summaryData.countyJailSummary.releasedIceTookCustody}</span>
                </div>
              </div>

              <div className="summaryCard">
                <h5>Housing Cost Analysis</h5>
                <div className="statRow">
                  <span>Total Bed Days:</span>
                  <span className="statValue">{summaryData.countyJailSummary.totalBedDays}</span>
                </div>
                <div className="statRow">
                  <span>Total Housing Cost:</span>
                  <span className="statValue">{formatCurrency(summaryData.countyJailSummary.totalHousingCost)}</span>
                </div>
                <div className="statRow">
                  <span>ICE Reimbursement:</span>
                  <span className="statValue">{formatCurrency(summaryData.countyJailSummary.totalReimbursement)}</span>
                </div>
                <div className="statRow">
                  <span>Reimbursement Gap:</span>
                  <span className={`statValue ${summaryData.countyJailSummary.reimbursementShortfall < 0 ? 'negative' : 'positive'}`}>
                    {formatCurrency(summaryData.countyJailSummary.reimbursementShortfall)}
                  </span>
                </div>
                <div className="statRow">
                  <span>Reimbursement Rate:</span>
                  <span className="statValue">
                    {formatPercentage(summaryData.countyJailSummary.totalReimbursement, summaryData.countyJailSummary.totalHousingCost)}
                  </span>
                </div>
              </div>
            </div>

            {/* Offense Classification Breakdown */}
            <div className="offenseBreakdown">
              <h5>Offense Classification Breakdown</h5>
              <div className="offenseGrid">
                {Object.entries(summaryData.countyJailSummary.offenseBreakdown).map(([offense, count]) => (
                  <div key={offense} className="offenseItem">
                    <span>{offense}:</span>
                    <span className="offenseCount">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FDOC Summary - Only show if there are FDOC records */}
          {summaryData.fdocSummary.totalRecords > 0 && (
            <div className="summarySection">
              <h4>Florida Department of Corrections Summary</h4>
              <div className="summaryGrid">
                <div className="summaryCard">
                  <h5>FDOC Inmate Data</h5>
                  <div className="statRow">
                    <span>Total Records:</span>
                    <span className="statValue">{summaryData.fdocSummary.totalRecords}</span>
                  </div>
                  <div className="statRow">
                    <span>Confirmed Aliens with ICE Detainer:</span>
                    <span className="statValue">{summaryData.fdocSummary.confirmedAliensWithDetainer}</span>
                  </div>
                  <div className="statRow">
                    <span>Confirmed Aliens without ICE Detainer:</span>
                    <span className="statValue">{summaryData.fdocSummary.confirmedAliensWithoutDetainer}</span>
                  </div>
                  <div className="statRow">
                    <span>ICE Detainers Removed:</span>
                    <span className="statValue">{summaryData.fdocSummary.detainersRemoved}</span>
                  </div>
                  <div className="statRow">
                    <span>Released to ICE Custody:</span>
                    <span className="statValue">{summaryData.fdocSummary.releasedToIce}</span>
                  </div>
                  <div className="statRow">
                    <span>Scheduled Release (30 days):</span>
                    <span className="statValue">{summaryData.fdocSummary.scheduledRelease30Days}</span>
                  </div>
                  <div className="statRow">
                    <span>Released (ICE Refused):</span>
                    <span className="statValue">{summaryData.fdocSummary.releasedIceRefused}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Facilities Reporting */}
          <div className="summarySection">
            <h4>Reporting Facilities</h4>
            <div className="facilitiesTable">
              <table>
                <thead>
                  <tr>
                    <th>Facility Name</th>
                    <th>County</th>
                    <th>Records Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.facilitiesReporting.map((facility, index) => (
                    <tr key={index}>
                      <td>{facility.name}</td>
                      <td>{facility.county}</td>
                      <td>{facility.records}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
            </>
          )}

          {viewMode === 'immigration' && (
            <>
              {/* IMMIGRATION VIEW CONTENT */}
              <div className="summarySection">
                <h4>Alien Contact Overview</h4>
                <div className="summaryGrid">
                  <div className="summaryCard">
                    <h5>Contact Summary</h5>
                    <div className="statRow">
                      <span>Total Alien Contacts:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.totalContacts || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>Arrests Made:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.arrested || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>ICE Contacted:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.iceContacted || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>ICE Responded:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.iceResponded || 0}</span>
                    </div>
                  </div>

                  <div className="summaryCard">
                    <h5>Record Status</h5>
                    <div className="statRow">
                      <span>Approved Records:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.approved || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>Pending Approval:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.pending || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>With Detention Records:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.withDetentionRecord || 0}</span>
                    </div>
                    <div className="statRow">
                      <span>Detained by ICE:</span>
                      <span className="statValue">{summaryData.immigrationBreakdowns?.contactTypes?.detainedByIce || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agency Breakdown */}
              <div className="summarySection">
                <h4>Contacts by Law Enforcement Agency</h4>
                <div className="facilitiesTable">
                  <table>
                    <thead>
                      <tr>
                        <th>Agency Name</th>
                        <th>Total Contacts</th>
                        <th>Arrests</th>
                        <th>ICE Contacted</th>
                        <th>With Detention</th>
                        <th>Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.immigrationBreakdowns?.byAgency && Object.entries(summaryData.immigrationBreakdowns.byAgency).map(([agency, stats]) => (
                        <tr key={agency}>
                          <td>{agency}</td>
                          <td>{stats.totalContacts}</td>
                          <td>{stats.arrested}</td>
                          <td>{stats.iceContacted}</td>
                          <td>{stats.withDetention}</td>
                          <td>{stats.approved}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* County Breakdown */}
              <div className="summarySection">
                <h4>Contacts by County</h4>
                <div className="facilitiesTable">
                  <table>
                    <thead>
                      <tr>
                        <th>County</th>
                        <th>Total Contacts</th>
                        <th>Arrests</th>
                        <th>ICE Contacted</th>
                        <th>With Detention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.immigrationBreakdowns?.byCounty && Object.entries(summaryData.immigrationBreakdowns.byCounty).map(([county, stats]) => (
                        <tr key={county}>
                          <td>{county}</td>
                          <td>{stats.totalContacts}</td>
                          <td>{stats.arrested}</td>
                          <td>{stats.iceContacted}</td>
                          <td>{stats.withDetention}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Citizenship Breakdown */}
              <div className="summarySection">
                <h4>Contacts by Country of Citizenship</h4>
                <div className="facilitiesTable">
                  <table>
                    <thead>
                      <tr>
                        <th>Country of Citizenship</th>
                        <th>Total Contacts</th>
                        <th>Arrests</th>
                        <th>ICE Contacted</th>
                        <th>With Detention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.immigrationBreakdowns?.byCitizenship && Object.entries(summaryData.immigrationBreakdowns.byCitizenship).map(([citizenship, stats]) => (
                        <tr key={citizenship}>
                          <td>{citizenship}</td>
                          <td>{stats.totalContacts}</td>
                          <td>{stats.arrested}</td>
                          <td>{stats.iceContacted}</td>
                          <td>{stats.withDetention}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="reportFooter">
            <p><strong>Report Generated:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>Reporting Period:</strong> {summaryData.reportMonth}</p>
            <p><strong>Next Report Due:</strong> 15th of next month</p>
          </div>
        </div>
      )}

















      {/* DIO Analysis Modal */}
      {showDIOAnalysisModal && (
        <div className="modal-overlay">
          <div className="modal-content export-modal">
            <div className="modal-header">
              <h3>DIO Analysis Report</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowDIOAnalysisModal(false)}
                disabled={exportLoading}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="export-options">

                {/* Month Selection Section */}
                <div className="export-month-selection">
                  <h4>Select Export Month</h4>
                  <input
                    type="month"
                    value={dioAnalysisSelectedMonth}
                    onChange={(e) => setDIOAnalysisSelectedMonth(e.target.value)}
                    className="month-input"
                    disabled={exportLoading}
                  />
                  {dioAnalysisSelectedMonth && (
                    <button
                      onClick={() => setDIOAnalysisSelectedMonth('')}
                      className="clear-month-btn"
                      disabled={exportLoading}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                {/* DIO Analysis Section */}
                <div className="export-section">
                  <h4>DIO Analysis Report</h4>
                  <div className="export-format-group">
                    <div className="format-section">
                      <h5>Immigration Contact Analysis</h5>
                      <p style={{ margin: '10px 0', color: '#666', fontSize: '14px' }}>
                        This report shows all immigration contacts for the selected month with a clear indication of which were DIO investigations vs regular contacts. Use this to verify your FDLE monthly summary statistics.
                      </p>
                      <div className="export-buttons">
                        <button
                          onClick={() => {
                            if (dioAnalysisSelectedMonth) {
                              exportDIOAnalysis(dioAnalysisSelectedMonth);
                              setShowDIOAnalysisModal(false);
                            }
                          }}
                          disabled={exportLoading || !dioAnalysisSelectedMonth}
                          className="modern-btn"
                          title={!dioAnalysisSelectedMonth ? 'Please select a month to generate DIO analysis' : 'Generate DIO analysis showing which contacts were DIO vs non-DIO investigations'}
                        >
                          {dioAnalysisSelectedMonth ? (
                            <>
                              Generate Report {dioAnalysisSelectedMonth}
                              <br />
                              (CSV: DIO vs Non-DIO)
                            </>
                          ) : (
                            <>
                              Generate DIO Analysis
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* TFM Arrest Export Modal */}
      {showTFMArrestModal && (
        <div className="modal-overlay">
          <div className="modal-content export-modal">
            <div className="modal-header">
              <h3>TFM Arrest Export</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowTFMArrestModal(false)}
                disabled={exportLoading}
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px', padding: '15px', background: '#e8f4fd', borderRadius: '6px', borderLeft: '4px solid var(--primary-color)' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#333' }}>
                  TFM Arrest Sheet Export
                </p>
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                  Export all records with complete TFM Arrest Sheet information for the selected month. Only includes records where "Is submitter DIO?" = Yes AND "Non-Criminal Arrest?" = Yes.
                </p>
              </div>

              <div className="export-modal-content">
                {/* Month Selection */}
                <div className="month-selection" style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                    Select Month to Export:
                  </label>
                  <select
                    value={tfmArrestSelectedMonth}
                    onChange={(e) => setTFMArrestSelectedMonth(e.target.value)}
                    className="modern-input"
                    disabled={exportLoading}
                    style={{ width: '100%', maxWidth: '300px' }}
                  >
                    <option value="">Select a month...</option>
                    {availableMonths.map((month) => (
                      <option key={month.month_key} value={month.month_key}>
                        {month.month_label.trim()}
                      </option>
                    ))}
                  </select>
                  {tfmArrestSelectedMonth && (
                    <button
                      onClick={() => setTFMArrestSelectedMonth('')}
                      className="clear-month-btn"
                      disabled={exportLoading}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>

                {/* TFM Arrest Export Section */}
                <div className="export-section">
                  <h4>TFM Arrest Sheet Records</h4>
                  <div className="export-format-group">
                    <div className="format-section">
                      <h5>CSV Export</h5>
                      <p style={{ margin: '10px 0', color: '#666', fontSize: '14px' }}>
                        Exports all 22 TFM Arrest Sheet fields including officer information, arrest details, alien information, consent/search details, and investigation hours. Only includes records where DIO = Yes AND Non-Criminal Arrest = Yes.
                      </p>
                      <div className="export-buttons">
                        <button
                          onClick={() => {
                            if (tfmArrestSelectedMonth) {
                              exportTFMArrestData(tfmArrestSelectedMonth);
                              setShowTFMArrestModal(false);
                            }
                          }}
                          disabled={exportLoading || !tfmArrestSelectedMonth}
                          className="modern-btn"
                          title={!tfmArrestSelectedMonth ? 'Please select a month to export TFM Arrest data' : 'Export records where DIO = Yes AND Non-Criminal Arrest = Yes with TFM Arrest Sheet information in CSV format'}
                        >
                          {tfmArrestSelectedMonth ? (
                            <>
                              Export {tfmArrestSelectedMonth}
                              <br />
                              (CSV: TFM Arrest Records)
                            </>
                          ) : (
                            <>
                              Export TFM Arrest
                              <br />
                              (Select Month)
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DetentionMonthlySummary;
